import React, { useState, useEffect } from 'react';
import { slpRecords, organizations, venues, studentsWithNumbers } from '../../Data/studentLearningProfiles';
import { slpTypes, eventTypes, achievementLevels, slpCategories } from '../../Data/studentLearningProfiles/slpConfig';
import { teachers } from '../../Data/teachers';

const SLPEventDetails = ({ record, onBack, onEdit, onConvertToAward }) => {
  if (!record) return null;

  const getOrganization = () => organizations.find(org => org.id === record.organization);
  const getVenue = () => venues.find(venue => venue.id === record.venue);
  const getSlpType = () => slpTypes[record.slpType];
  const getEventType = () => eventTypes[record.eventType];
  const getAchievementLevel = () => record.achievementLevel ? achievementLevels[record.achievementLevel] : null;
  const getLevel = () => record.level ? slpCategories.level[record.level] : null;
  const getRole = () => record.role ? slpCategories.role[record.role] : null;

  const getStudents = () => {
    if (!record.students?.length) return [];
    return record.students.map(studentNumber => 
      studentsWithNumbers.find(s => s.studentNumber === studentNumber)
    ).filter(Boolean);
  };

  const getTeachers = () => {
    if (!record.teachersInCharge?.length) return [];
    return record.teachersInCharge.map(teacherId => 
      teachers.find(t => t.id === teacherId)
    ).filter(Boolean);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('zh-HK', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      weekday: 'long'
    });
  };

  const formatTime = (timeString) => {
    if (!timeString) return '';
    return timeString.substring(0, 5);
  };

  const canConvertToAward = record.slpType === 'ole' && getSlpType()?.canConvertToAward;

  return (
    <div style={{ 
      padding: '20px', 
      backgroundColor: '#f8f9fa', 
      minHeight: '100vh',
      fontFamily: 'Arial, sans-serif'
    }}>
      {/* Header */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: '20px',
        padding: '15px',
        backgroundColor: 'white',
        borderRadius: '8px',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
      }}>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <button 
            onClick={onBack}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '24px',
              cursor: 'pointer',
              marginRight: '15px',
              color: '#007bff'
            }}
          >
            ← 
          </button>
          <div>
            <h2 style={{ margin: 0, color: '#333' }}>SLP 記錄詳情</h2>
            <p style={{ margin: '5px 0 0 0', color: '#666', fontSize: '14px' }}>
              SLP編號: {record.slpNumber}
            </p>
          </div>
        </div>

        <div style={{ display: 'flex', gap: '10px' }}>
          {canConvertToAward && (
            <button
              onClick={() => onConvertToAward(record)}
              style={{
                padding: '10px 16px',
                backgroundColor: '#ffc107',
                color: '#212529',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: 'bold'
              }}
            >
              🏆 轉換為獎項
            </button>
          )}
          
          <button
            onClick={onEdit}
            style={{
              padding: '10px 16px',
              backgroundColor: '#007bff',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: 'bold'
            }}
          >
            ✏️ 編輯
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div style={{
        backgroundColor: 'white',
        borderRadius: '8px',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        overflow: 'hidden'
      }}>
        {/* Title Banner */}
        <div style={{
          padding: '25px',
          background: `linear-gradient(135deg, ${getSlpType()?.color || '#007bff'}, ${getSlpType()?.color || '#007bff'}dd)`,
          color: 'white'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', marginBottom: '10px' }}>
            <span style={{ fontSize: '32px', marginRight: '15px' }}>
              {getSlpType()?.icon}
            </span>
            <div>
              <h1 style={{ margin: 0, fontSize: '28px' }}>{record.title}</h1>
              <p style={{ margin: '5px 0 0 0', fontSize: '16px', opacity: 0.9 }}>
                {getSlpType()?.name} - {getEventType()?.name}
              </p>
            </div>
          </div>

          {/* Status and Achievement */}
          <div style={{ display: 'flex', gap: '15px', alignItems: 'center' }}>
            <span style={{
              padding: '6px 12px',
              backgroundColor: 'rgba(255,255,255,0.2)',
              borderRadius: '15px',
              fontSize: '14px'
            }}>
              狀態: {record.status === 'completed' ? '已完成' : 
                     record.status === 'scheduled' ? '已安排' : 
                     record.status === 'draft' ? '草稿' : record.status}
            </span>

            {getAchievementLevel() && (
              <span style={{
                padding: '6px 12px',
                backgroundColor: 'rgba(255,255,255,0.2)',
                borderRadius: '15px',
                fontSize: '14px'
              }}>
                {getAchievementLevel().icon} {getAchievementLevel().name}
              </span>
            )}

            <span style={{
              padding: '6px 12px',
              backgroundColor: 'rgba(255,255,255,0.2)',
              borderRadius: '15px',
              fontSize: '14px'
            }}>
              {record.duration}小時
            </span>
          </div>
        </div>

        {/* Details Grid */}
        <div style={{ padding: '25px' }}>
          <div style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: '30px',
            marginBottom: '30px'
          }}>
            {/* Left Column */}
            <div>
              {/* Date and Time */}
              <div style={{ marginBottom: '25px' }}>
                <h3 style={{ 
                  margin: '0 0 15px 0', 
                  color: '#333',
                  borderBottom: '2px solid #007bff',
                  paddingBottom: '8px'
                }}>
                  📅 日期時間
                </h3>
                <div style={{ fontSize: '16px', lineHeight: '1.6' }}>
                  <div style={{ marginBottom: '8px' }}>
                    <strong>日期:</strong> {formatDate(record.date)}
                  </div>
                  {record.timeFrom && record.timeTo && (
                    <div style={{ marginBottom: '8px' }}>
                      <strong>時間:</strong> {formatTime(record.timeFrom)} - {formatTime(record.timeTo)}
                    </div>
                  )}
                  <div>
                    <strong>時數:</strong> {record.duration} 小時
                  </div>
                </div>
              </div>

              {/* Organization and Venue */}
              <div style={{ marginBottom: '25px' }}>
                <h3 style={{ 
                  margin: '0 0 15px 0', 
                  color: '#333',
                  borderBottom: '2px solid #28a745',
                  paddingBottom: '8px'
                }}>
                  🏢 機構地點
                </h3>
                <div style={{ fontSize: '16px', lineHeight: '1.6' }}>
                  <div style={{ marginBottom: '8px' }}>
                    <strong>主辦機構:</strong> {getOrganization()?.name || record.organization}
                    {getOrganization()?.nameEn && (
                      <div style={{ fontSize: '14px', color: '#666', marginTop: '2px' }}>
                        {getOrganization().nameEn}
                      </div>
                    )}
                  </div>
                  <div>
                    <strong>活動地點:</strong> {getVenue()?.name || record.venue}
                    {getVenue()?.nameEn && (
                      <div style={{ fontSize: '14px', color: '#666', marginTop: '2px' }}>
                        {getVenue().nameEn}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Participation Details */}
              <div>
                <h3 style={{ 
                  margin: '0 0 15px 0', 
                  color: '#333',
                  borderBottom: '2px solid #17a2b8',
                  paddingBottom: '8px'
                }}>
                  👥 參與詳情
                </h3>
                <div style={{ fontSize: '16px', lineHeight: '1.6' }}>
                  {getLevel() && (
                    <div style={{ marginBottom: '8px' }}>
                      <strong>參與層次:</strong> {getLevel().name}
                      <span style={{ fontSize: '14px', color: '#666', marginLeft: '8px' }}>
                        ({getLevel().nameEn})
                      </span>
                    </div>
                  )}
                  {getRole() && (
                    <div>
                      <strong>參與角色:</strong> {getRole().name}
                      <span style={{ fontSize: '14px', color: '#666', marginLeft: '8px' }}>
                        ({getRole().nameEn})
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Right Column */}
            <div>
              {/* Students */}
              <div style={{ marginBottom: '25px' }}>
                <h3 style={{ 
                  margin: '0 0 15px 0', 
                  color: '#333',
                  borderBottom: '2px solid #6f42c1',
                  paddingBottom: '8px'
                }}>
                  🎓 參與學生 ({getStudents().length} 人)
                </h3>
                
                {getStudents().length > 0 ? (
                  <div style={{
                    maxHeight: '200px',
                    overflowY: 'auto',
                    border: '1px solid #eee',
                    borderRadius: '6px',
                    padding: '10px'
                  }}>
                    {getStudents().map((student, index) => (
                      <div
                        key={student.studentNumber}
                        style={{
                          padding: '8px',
                          borderBottom: index < getStudents().length - 1 ? '1px solid #f0f0f0' : 'none',
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center'
                        }}
                      >
                        <div>
                          <div style={{ fontWeight: 'bold', fontSize: '15px' }}>
                            {student.name}
                          </div>
                          <div style={{ fontSize: '12px', color: '#666' }}>
                            {student.class} | {student.studentNumber}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div style={{ 
                    padding: '20px', 
                    textAlign: 'center', 
                    color: '#666',
                    fontStyle: 'italic'
                  }}>
                    暫無學生記錄
                  </div>
                )}
              </div>

              {/* Teachers */}
              <div>
                <h3 style={{ 
                  margin: '0 0 15px 0', 
                  color: '#333',
                  borderBottom: '2px solid #fd7e14',
                  paddingBottom: '8px'
                }}>
                  👨‍🏫 負責老師 ({getTeachers().length} 人)
                </h3>
                
                {getTeachers().length > 0 ? (
                  <div>
                    {getTeachers().map((teacher, index) => (
                      <div
                        key={teacher.id}
                        style={{
                          padding: '8px 12px',
                          backgroundColor: '#f8f9fa',
                          borderRadius: '6px',
                          marginBottom: index < getTeachers().length - 1 ? '8px' : '0',
                          border: '1px solid #e9ecef'
                        }}
                      >
                        <div style={{ fontWeight: 'bold', fontSize: '15px' }}>
                          {teacher.name}
                        </div>
                        {teacher.subject && (
                          <div style={{ fontSize: '12px', color: '#666' }}>
                            {teacher.subject}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div style={{ 
                    padding: '20px', 
                    textAlign: 'center', 
                    color: '#666',
                    fontStyle: 'italic'
                  }}>
                    暫無負責老師記錄
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Remarks */}
          {record.remarks && (
            <div style={{ marginTop: '30px' }}>
              <h3 style={{ 
                margin: '0 0 15px 0', 
                color: '#333',
                borderBottom: '2px solid #6c757d',
                paddingBottom: '8px'
              }}>
                📝 備註
              </h3>
              <div style={{
                padding: '15px',
                backgroundColor: '#f8f9fa',
                borderRadius: '6px',
                border: '1px solid #e9ecef',
                fontSize: '15px',
                lineHeight: '1.6',
                whiteSpace: 'pre-wrap'
              }}>
                {record.remarks}
              </div>
            </div>
          )}

          {/* Metadata */}
          <div style={{
            marginTop: '30px',
            padding: '15px',
            backgroundColor: '#f8f9fa',
            borderRadius: '6px',
            border: '1px solid #e9ecef'
          }}>
            <h4 style={{ margin: '0 0 10px 0', color: '#666' }}>記錄資訊</h4>
            <div style={{ fontSize: '12px', color: '#666', lineHeight: '1.4' }}>
              {record.createdAt && (
                <div>建立時間: {new Date(record.createdAt).toLocaleString('zh-HK')}</div>
              )}
              {record.updatedAt && record.updatedAt !== record.createdAt && (
                <div>更新時間: {new Date(record.updatedAt).toLocaleString('zh-HK')}</div>
              )}
              <div>記錄ID: {record.id}</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SLPEventDetails; 